import { useEffect, useState } from 'react'

import { useIncidentCreationData } from '../hooks/useIncidentCreationData'
import { UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, ColorScheme, Flex, Text } from '@hivewatch/dark'

import { ControlledSelect, DarkAddressSearch } from '@/components/ui'
import { Facility } from '@/graphql/generated/schemas'
import { getDeviceOptions, getFloorOptions } from '@/utils/forms/optionBuilders'

import { FormInputsIProps } from '../types/types'
import { isDeviceHealthSelected } from '../utils/utils'

interface DarkNormalIncidentFormIProps {
  form: UseFormMethods<FormInputsIProps>
  facility: Facility
  colorScheme: ColorScheme
}

/**
 * Rebrand variant of {@link NormalIncidentForm}. Floor and device selection,
 * the Device Health validation rules, and the read-only facility address are
 * unchanged — only the field chrome moves to the design system.
 */
export const DarkNormalIncidentForm = ({
  form,
  facility,
  colorScheme,
}: DarkNormalIncidentFormIProps) => {
  const { t } = useTranslation()
  const { watch, control, reset, getValues } = form
  const [floorId, setFloorId] = useState<string | null>(null)
  const [incidentCategoryId, setIncidentCategoryId] = useState<string | null>(
    null
  )

  const { isFloorsLoading, floorsData, devicesData, categoriesData } =
    useIncidentCreationData({ facilityId: facility?.id, floorId })

  const floorOptions = getFloorOptions(floorsData)
  const deviceOptions = getDeviceOptions(devicesData)

  const watchIncidentCategoryId = watch('incidentCategory')
  const watchFloorOrigin = watch('floorOrigin')

  const isDeviceHealth = isDeviceHealthSelected(
    categoriesData,
    incidentCategoryId
  )

  useEffect(() => {
    if (watchIncidentCategoryId) {
      setIncidentCategoryId(watchIncidentCategoryId.value ?? null)
    }
  }, [watchIncidentCategoryId])

  useEffect(() => {
    if (watchFloorOrigin) {
      setFloorId(watchFloorOrigin.value)
      reset({
        ...getValues(),
        deviceOrigin: null,
      })
    }
  }, [watchFloorOrigin])

  const labelColor =
    colorScheme === 'light' ? 'light.text.primary' : 'text.primary'

  return (
    <Flex direction='column' gap='16px'>
      <ControlledSelect
        colorScheme={colorScheme}
        control={control}
        data-testid='createIncidentModal_floorName'
        isClearable
        isLoading={isFloorsLoading}
        label={
          isDeviceHealth
            ? t('incidents:incident_create_floor_label_required')
            : t('incidents:incident_create_floor_label_optional')
        }
        name='floorOrigin'
        noOptionsMessage={() =>
          !facility?.id
            ? t('incidents:incident_create_floor_no_facility')
            : t('incidents:incident_create_floor_none')
        }
        options={floorOptions}
        placeholder={t('incidents:incident_create_floor_placeholder')}
        rules={{
          required: isDeviceHealth
            ? t('incidents:incident_create_floor_required')
            : false,
        }}
      />

      {facility?.address && (
        // AddressSearch has no dark label prop, so render one to match.
        <Box>
          <Text color={labelColor} mb='4px' textStyle='label'>
            {t('common:address')}
          </Text>
          <DarkAddressSearch
            colorScheme={colorScheme}
            defaultValue={facility?.address?.[0]}
            disabled
            showTooltip={false}
          />
        </Box>
      )}

      {isDeviceHealth && (
        <ControlledSelect
          colorScheme={colorScheme}
          control={control}
          label={t('incidents:incident_create_device_label')}
          name='deviceOrigin'
          options={deviceOptions}
          placeholder={t('incidents:incident_create_device_placeholder')}
          rules={{
            validate: (value) => {
              const currentCategory = getValues('incidentCategory')
              return !(currentCategory?.label === 'Device Health' && !value)
            },
          }}
        />
      )}
    </Flex>
  )
}
